import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";

const FADE_DURATION = 300;

const Toast = ({ type = "info", message, onClose, autoClose = 3000 }) => {
	const [visible, setVisible] = useState(false);
	const [progress, setProgress] = useState(100);

	const paused = useRef(false);
	const remainingTime = useRef(autoClose);
	const startTime = useRef(Date.now());
	const intervalRef = useRef(null);

	useEffect(() => setVisible(true), []);

	// TIMER + PERFECT PAUSE LOGIC
	useEffect(() => {
		if (!onClose || !autoClose) return;

		startTime.current = Date.now();

		const tick = () => {
			const elapsed = Date.now() - startTime.current;
			const left = Math.max(remainingTime.current - elapsed, 0);

			// Update progress only if not paused
			if (!paused.current) {
				setProgress((left / autoClose) * 100);
			}

			// Do NOT auto-close while paused
			if (!paused.current && left <= 0) {
				clearInterval(intervalRef.current);
				closeNow();
			}
		};

		intervalRef.current = setInterval(tick, 50);

		return () => clearInterval(intervalRef.current);
	}, [autoClose, onClose]);

	const pauseTimer = () => {
		paused.current = true;
		remainingTime.current -= Date.now() - startTime.current;
	};

	const resumeTimer = () => {
		paused.current = false;
		startTime.current = Date.now();
	};

	const closeNow = () => {
		// Prevent closing during hover
		if (paused.current) return;

		setVisible(false);
		setTimeout(onClose, FADE_DURATION);
	};

	const overlayClick = (e) => {
		if (e.target.id === "toast-overlay") closeNow();
	};

	const types = {
		success: {
			color: "text-green-600",
			bg: "bg-green-100",
			bar: "bg-green-600",
			icon: (
				<svg
					className="w-6 h-6"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
				>
					<path
						d="M5 13l4 4L19 7"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			),
		},
		error: {
			color: "text-red-600",
			bg: "bg-red-100",
			bar: "bg-red-600",
			icon: (
				<svg
					className="w-6 h-6"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
				>
					<path
						d="M6 18L18 6M6 6l12 12"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			),
		},
		info: {
			color: "text-sky-600",
			bg: "bg-sky-100",
			bar: "bg-sky-600",
			icon: (
				<svg
					className="w-6 h-6"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
				>
					<path
						d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			),
		},
		warning: {
			color: "text-yellow-600",
			bg: "bg-yellow-100",
			bar: "bg-yellow-600",
			icon: (
				<svg
					className="w-6 h-6"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
				>
					<path
						d="M12 9v2m0 4h.01M4.93 19h14.14L12 4 4.93 19z"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			),
		},
	};

	const style = types[type] || types.info;

	return ReactDOM.createPortal(
		<div
			id="toast-overlay"
			onClick={overlayClick}
			className="fixed inset-0 bg-black/40 flex justify-end items-end z-[9999]"
		>
			<div
				onMouseEnter={pauseTimer}
				onMouseLeave={resumeTimer}
				className={`relative rounded-xl shadow-xl bg-white w-fit min-w-[300px] mr-6 mb-6 overflow-hidden transition-opacity duration-300 ${
					visible ? "opacity-100" : "opacity-0"
				}`}
			>
				<div className="absolute top-0 left-0 w-full h-[4px] bg-gray-300">
					<div
						className={`h-full ${style.bar}`}
						style={{ width: `${progress}%` }}
					/>
				</div>

				<div className="flex items-center gap-4 p-4">
					<div
						className={`w-10 h-10 rounded-full flex justify-center items-center ${style.bg} ${style.color}`}
					>
						{style.icon}
					</div>

					<div className="flex-grow text-[15px] font-medium text-gray-800">
						{message}
					</div>

					<button onClick={closeNow} className="text-gray-600 text-xl">
						✕
					</button>
				</div>
			</div>
		</div>,
		document.body
	);
};

export default Toast;
